import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Chip, Typography } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodePresetChips — Row of template preset chips.
 * Used by: AI Assistant (16 template presets).
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const ChipsContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
});

const PresetChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  fontSize: NODE_UI.fontSize.caption,
  height: 26,
  borderRadius: NODE_UI.borderRadiusSm,
  fontWeight: isSelected ? 600 : NODE_UI.fontWeight.value,
  backgroundColor: isSelected
    ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e8e8e8')
    : 'transparent',
  border: `1px solid ${isSelected ? theme.palette.text.primary : theme.palette.divider}`,
  color: isSelected ? theme.palette.text.primary : theme.palette.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.text.secondary,
  },
}));

const NodePresetChips = React.memo(function NodePresetChips({
  label,
  presets = [],
  selectedValue,
  onSelect,
  sx,
}) {
  const handleClick = useCallback((preset, e) => {
    stopPropagation(e);
    onSelect?.(preset.value ?? preset.label ?? preset);
  }, [onSelect]);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <ChipsContainer sx={sx}>
        {presets.map((preset) => {
          const val = preset.value ?? preset.label ?? preset;
          const lbl = preset.label ?? preset;
          return (
            <PresetChip
              key={val}
              label={lbl}
              isSelected={selectedValue === val}
              onClick={(e) => handleClick(preset, e)}
              variant="outlined"
              size="small"
            />
          );
        })}
      </ChipsContainer>
    </div>
  );
});

export default NodePresetChips;
